/*
  "What needs me?" — five focus counters above the board (idea 5): Assigned to me · Due today · Overdue ·
  Blocked · Needs details. Each is a filter: one click shows only those tasks, a second click shows all
  again (kept in the URL as ?focus=…, so the view can be sent as a link). Phones get the same five as a
  row of chips ("Mine 2 · Today 3 · ● Overdue 2 …") that fades out at the right edge while more chips follow.

  Five across only from 1280px (xl); narrower screens (a 1024px laptop with the sidebar open, a tablet) get
  3 + 2 so labels and meta lines stay readable. A meta line keeps its tail (" · 5 Oct", " +2") and cuts the
  name instead.
*/
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { CalendarDays, Inbox, OctagonAlert, TriangleAlert, UserRound } from 'lucide-react';
import { cn } from '@/lib/utils';
import { StatCard } from '@/components/ui/stat-card';
import { FOCUS_OPTIONS } from './taskModel';

const ICONS = { mine: UserRound, today: CalendarDays, overdue: TriangleAlert, blocked: OctagonAlert, details: Inbox };
const COUNT_TONE = { overdue: 'red', blocked: 'red', details: 'amber', today: 'amber' };
// 6-column grid below xl: the first three take 2 columns, the last two 3 (two full rows); xl: five equal ones.
const SPAN = ['col-span-2', 'col-span-2', 'col-span-2', 'col-span-3', 'col-span-3'];
// The chips row fades into the page at its right edge while more chips are off screen.
const FADE = '[-webkit-mask-image:linear-gradient(to_right,#000_calc(100%-48px),transparent)] [mask-image:linear-gradient(to_right,#000_calc(100%-48px),transparent)]';

/** "Next: Order GRMS controllers · 5 Oct": the name truncates, the tail stays. */
function MetaLine({ meta, tail }) {
  if (!tail || !String(meta || '').endsWith(tail)) return meta;
  return (
    <span className="flex min-w-0" title={meta}>
      <span className="min-w-0 truncate">{meta.slice(0, meta.length - tail.length)}</span>
      <span className="shrink-0 whitespace-pre">{tail}</span>
    </span>
  );
}

function PhoneChips({ summary, value, onChange, loading }) {
  const scrollerRef = useRef(null);
  const [atEnd, setAtEnd] = useState(true);
  const measure = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 2);
  }, []);
  useLayoutEffect(() => {
    measure();
    if (typeof ResizeObserver === 'undefined' || !scrollerRef.current) return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(scrollerRef.current);
    return () => observer.disconnect();
  }, [measure, loading]);

  return (
    <div
      ref={scrollerRef}
      onScroll={measure}
      className={cn('-mx-4 flex gap-2 overflow-x-auto px-4 pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden', !atEnd && FADE)}
      role="group"
      aria-label="Focus on"
      data-testid="task-focus-chips"
      data-more={atEnd ? undefined : 'true'}
    >
      {FOCUS_OPTIONS.map((option) => {
        const item = summary[option.key] || { count: 0 };
        const active = value === option.key;
        const tone = item.count ? COUNT_TONE[option.key] : null;
        return (
          <button
            key={option.key}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(active ? '' : option.key)}
            className={cn(
              'inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full border px-3.5 text-[13.5px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              active ? 'border-[color:var(--brand-ink)] bg-brand-soft text-brand-ink' : 'border-line bg-surface text-ink shadow-card'
            )}
            data-testid={`task-focus-${option.key}`}
          >
            {option.key === 'overdue' && item.count ? <span className="ui-tone-dot tone-red" aria-hidden="true" /> : null}
            {option.short}
            <b className={cn('font-extrabold tabular-nums', tone === 'red' && 'text-status-red-ink', tone === 'amber' && 'text-status-amber-ink')}>
              {loading ? '–' : item.count}
            </b>
          </button>
        );
      })}
    </div>
  );
}

export default function TaskFocusCounters({ summary, value, onChange, loading = false, isPhone = false }) {
  if (isPhone) return <PhoneChips summary={summary} value={value} onChange={onChange} loading={loading} />;
  return (
    <div className="grid grid-cols-6 gap-3 xl:grid-cols-5" role="group" aria-label="Focus on" data-testid="task-focus-counters">
      {FOCUS_OPTIONS.map((option, index) => {
        const item = summary[option.key] || { count: 0, meta: '' };
        const active = value === option.key;
        return (
          <StatCard
            key={option.key}
            label={option.label}
            icon={ICONS[option.key]}
            tone={option.tone}
            value={item.count}
            valueTone={item.count ? COUNT_TONE[option.key] : undefined}
            meta={item.meta ? <MetaLine meta={item.meta} tail={item.tail} /> : null}
            loading={loading}
            onClick={() => onChange(active ? '' : option.key)}
            aria-pressed={active}
            title={active ? `Showing ${option.label.toLowerCase()} — click to show everything` : `Show only: ${option.label.toLowerCase()}`}
            size="compact"
            className={cn(
              'gap-1.5',
              SPAN[index] || 'col-span-2',
              'xl:col-span-1',
              active && 'border-[color:var(--brand-ink)] bg-brand-soft shadow-raised hover:border-[color:var(--brand-ink)]'
            )}
            data-testid={`task-focus-${option.key}`}
          />
        );
      })}
    </div>
  );
}
